import { ReactNode, useMemo } from 'react'
import classNames from '@ui-components-3/ui/lib/utils/classNames'
import { formatTicketDate } from './util'
import type { CustomField, TicketDetails, Ticket } from './types'
import { HEADER_FIELD_IDS, HEADER_FIELDS } from './constants/crisisNotes'

type LabelValueProps = {
  label: string
  children?: ReactNode
  className?: string
}

const LabelValue = ({ label, children, className }: LabelValueProps) => {
  const displayValue = children === null || children === undefined || children === '' ? '-' : children

  return (
    <div className={classNames('flex h-full flex-col items-start gap-1 min-w-0', className)}>
      <h6 className="typography-body font-semibold text-neutral-500">{label}</h6>
      <p className="typography-body-l font-medium text-neutral-800 w-full break-words overflow-wrap-anywhere">
        {displayValue}
      </p>
    </div>
  )
}

type CaseManagementDetailsViewProps = {
  details: TicketDetails
  assignee: string
  ticketData?: Ticket
}

const CaseManagementDetailsView = ({ details, assignee, ticketData }: CaseManagementDetailsViewProps) => {
  const customFieldsMap = useMemo(() => {
    const map = new Map<string, CustomField>()
    if (details.customFields) {
      details.customFields.forEach((field) => {
        map.set(field.fieldId, field)
      })
    }
    return map
  }, [details.customFields])

  const renderFieldValue = (field: CustomField) => {
    // Basic field rendering - add specific cases as needed
    if (!field.value) return null

    // Handle date fields that might need formatting
    if (field.fieldName.toLowerCase().includes('date') || field.fieldName.toLowerCase().includes('time')) {
      try {
        // Try to format as date if it looks like a date
        const formattedDate = formatTicketDate(field.value, true)
        return <span className="whitespace-pre">{formattedDate}</span>
      } catch {
        // If formatting fails, just return the raw value
        return field.value
      }
    }

    // Handle multi-line text fields
    if (field.value.includes('\n') || field.value.includes(',')) {
      return <span className="block whitespace-pre-line">{field.value.split(',').join('\n')}</span>
    }

    return field.value
  }

  return (
    <>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {HEADER_FIELDS.map((fieldId) => {
          const field = customFieldsMap.get(fieldId)
          if (!field) return null

          // Only use memberId fallback for MEMBER_ID field
          const displayValue =
            fieldId === HEADER_FIELD_IDS.MEMBER_ID ? field?.value || ticketData?.memberId || '-' : field?.value || '-'

          return (
            <LabelValue key={fieldId} label={field.fieldName}>
              {displayValue}
            </LabelValue>
          )
        })}
      </div>
      <hr />
      <div className="flex flex-row flex-wrap items-start justify-between gap-x-6 gap-y-2">
        <h2 className="typography-h4">Case Management Note</h2>
        <p className="typography-body">
          <b className="mr-2 font-medium">Assignee:</b>
          {assignee}
        </p>
      </div>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {details.customFields
          ?.filter((field) => !HEADER_FIELDS.includes(field.fieldId))
          .map((field) => (
            <LabelValue key={field.fieldId} label={field.fieldName}>
              {renderFieldValue(field)}
            </LabelValue>
          ))}
      </div>
    </>
  )
}

export default CaseManagementDetailsView
